import { Check, Clock, Close, Warning, type IconGlyph } from "@/icons";
import { BottomSheet } from "@/components/modals/BottomSheet";
import { Button, Callout, SheetBody, Text, Timeline, TimelineStep } from "@/components/ui";
import { formatBRL } from "@/lib/format-brl";
import {
  getCancellationDeniedReason,
  getCancellationReasonMessage,
  getTotalRefundedAmount,
  hasSettledRefund,
  isAwaitingPixPayment,
} from "@/lib/order-status";
import { OrderResponse } from "@/types/external";
import { OrderStatusType } from "@/types/order-status";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface OrderTimelineModalProps {
  order: OrderResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canResumePix?: boolean;
  onShowQr?: () => void;
}

const statusConfig: Record<OrderStatusType, {
  label: string;
  description: string;
  icon: IconGlyph;
}> = {
  CREATING: {
    label: "Realizando o pedido",
    description: "Seu pedido está sendo processado",
    icon: Clock
  },
  CREATED: {
    label: "Pedido realizado",
    description: "Aguardando confirmação da loja",
    icon: Check
  },
  CONFIRMED: {
    label: "Pedido confirmado",
    description: "A loja confirmou seu pedido",
    icon: Check
  },
  PREPARATION_REQUESTED: {
    label: "Preparo solicitado",
    description: "O preparo do pedido foi solicitado",
    icon: Clock
  },
  PREPARING: {
    label: "Em preparo",
    description: "Estamos preparando seu pedido",
    icon: Clock
  },
  READY_FOR_PICKUP: {
    label: "Pronto para retirada",
    description: "Seu pedido está pronto e aguardando sua retirada.",
    icon: Check
  },
  PICKUP_AREA_ASSIGNED: {
    label: "Área de retirada definida",
    description: "Dirija-se à área designada para retirar seu pedido",
    icon: Check
  },
  DISPATCHED: {
    label: "Saiu para entrega",
    description: "Pedido está a caminho",
    icon: Clock
  },
  DELIVERED: {
    label: "Entregue",
    description: "Pedido foi entregue com sucesso",
    icon: Check
  },
  PICKED_UP: {
    label: "Retirado",
    description: "Pedido foi retirado com sucesso",
    icon: Check
  },
  CONCLUDED: {
    label: "Concluído",
    description: "Pedido finalizado",
    icon: Check
  },
  CANCELLATION_REQUESTED: {
    label: "Cancelamento solicitado",
    description: "Solicitação de cancelamento em análise",
    icon: Clock
  },
  CANCELLATION_REQUEST_DENIED: {
    label: "Cancelamento negado",
    description: "",
    icon: Warning
  },
  ORDER_CANCELLATION_REQUEST: {
    label: "Solicitação de cancelamento",
    description: "A loja solicitou o cancelamento do pedido",
    icon: Clock
  },
  CANCELLED_DENIED: {
    label: "Cancelamento negado",
    description: "",
    icon: Warning
  },
  CANCELLED: {
    label: "Cancelado",
    description: "Pedido foi cancelado",
    icon: Close
  },
  REFUND_PROCESSING: {
    label: "Estornando via Pix",
    description: "Cancelamento aprovado. Estamos processando o estorno via Pix, isso pode levar alguns instantes.",
    icon: Clock
  }
};

const indoorOverrides: Partial<
  Record<OrderStatusType, { label: string; description: string }>
> = {
  READY_FOR_PICKUP: {
    label: "Pronto, a caminho da mesa",
    description: "Seu pedido ficou pronto e será levado até a mesa",
  },
  PICKED_UP: {
    label: "Entregue",
    description: "Pedido entregue na mesa",
  },
};

const deliveryOverrides: Partial<
  Record<OrderStatusType, { label: string; description: string }>
> = {
  READY_FOR_PICKUP: {
    label: "Pedido pronto",
    description: "Seu pedido está pronto, aguardando um entregador fazer a retirada.",
  },
};

export default function OrderTimelineModal({ order, open, onOpenChange, canResumePix = false, onShowQr }: OrderTimelineModalProps) {
  const events = order.events || [];
  const sortedEvents = [...events].sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  type TimelineItem = {
    key: string;
    label: string;
    description: string;
    /** Merchant deny free text — small "Motivo: …" under the denied step. */
    denialReason?: string | null;
    /**
     * pix-refund: "Estorno de R$X realizado" shown alongside (not instead of)
     * the cancellation reason on the CANCELLED step, since a settled refund
     * is now the common case for a captured PIX_ONLINE cancellation.
     */
    refundNote?: string | null;
    icon: IconGlyph;
    time: Date | null;
    isCancelled: boolean;
    isDenied: boolean;
    isTerminal: boolean;
  };
  const isIndoor = order.type === "INDOOR";
  const isDelivery = order.type === "DELIVERY";
  const cancellationReason = getCancellationReasonMessage(order);
  const denialReason = getCancellationDeniedReason(order);
  const refundedAmount = hasSettledRefund(order) ? getTotalRefundedAmount(order) : null;
  const items: TimelineItem[] = sortedEvents.map((event) => {
    const status = event.status as OrderStatusType;
    const config = statusConfig[status];
    const override = isIndoor
      ? indoorOverrides[status]
      : isDelivery
        ? deliveryOverrides[status]
        : undefined;
    const isDenied =
      status === "CANCELLATION_REQUEST_DENIED" || status === "CANCELLED_DENIED";
    const description =
      status === "CANCELLED" && cancellationReason
        ? cancellationReason
        : override?.description ?? config?.description ?? "Status do pedido atualizado";
    const refundNote =
      status === "CANCELLED" && refundedAmount !== null
        ? `Estorno de ${formatBRL(refundedAmount)} realizado`
        : null;
    return {
      key: event.id,
      label: override?.label ?? config?.label ?? status,
      description,
      denialReason: isDenied ? denialReason : null,
      refundNote,
      icon: config?.icon || Clock,
      time: new Date(event.createdAt),
      isCancelled: status === "CANCELLED",
      isDenied,
      isTerminal: ["DELIVERED", "PICKED_UP", "CONCLUDED"].includes(status),
    };
  });
  if (isAwaitingPixPayment(order)) {
    items.push({
      key: "awaiting-pix-payment",
      label: "Aguardando pagamento",
      description: "Pague o Pix para a loja confirmar seu pedido.",
      icon: Clock,
      time: null,
      isCancelled: false,
      isDenied: false,
      isTerminal: false,
    });
  }

  const indoorForecastTime = (() => {
    const created = order.events?.find((e) => e.status === "CREATED")?.createdAt;
    if (!created) return null;
    return new Date(new Date(created).getTime() + 60 * 60 * 1000).toISOString();
  })();
  const forecast =
    order.deliveries && order.deliveries.length > 0
      ? {
        title: "Previsão de entrega",
        time: order.deliveries[0].Delivery.EstimatedDeliveryDateTime,
      }
      : order.OrderTakeouts && order.OrderTakeouts.length > 0
        ? {
          title: "Previsão de retirada",
          time: order.OrderTakeouts[0].Takeout.TakeoutDateTime,
        }
        : isIndoor && indoorForecastTime
          ? {
            title: "Previsão de entrega",
            time: indoorForecastTime,
          }
          : null;

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title="Acompanhar pedido" onClose={() => onOpenChange(false)}>
      <SheetBody maxHeight="md" className="pb-6">
        <Timeline>
          {items.map((item, index) => (
            <TimelineStep
              key={item.key}
              icon={item.icon}
              state={index < items.length - 1 || item.isTerminal ? "done" : "current"}
              tone={item.isCancelled ? "danger" : item.isDenied ? "warning" : "primary"}
              title={item.label}
              time={item.time ? format(item.time, "HH:mm", { locale: ptBR }) : undefined}
              dateTime={item.time?.toISOString()}
              description={item.description || undefined}
            >
              {item.denialReason && (
                <Callout tone="warning" size="sm" role="none">
                  <Text as="span" weight="semibold" tone="inherit">
                    Motivo:
                  </Text>{" "}
                  {item.denialReason}
                </Callout>
              )}
              {item.refundNote && (
                <Text size="sm" weight="medium" tone="default">
                  {item.refundNote}
                </Text>
              )}
              {item.time && (
                <Text size="xs" tone="subtle">
                  {format(item.time, "dd 'de' MMMM", { locale: ptBR })}
                </Text>
              )}
            </TimelineStep>
          ))}
        </Timeline>

        {canResumePix && onShowQr && (
          <div className="mt-6">
            <Button
              type="button"
              size="lg"
              fullWidth
              onClick={() => {
                onShowQr();
                onOpenChange(false);
              }}
            >
              Exibir QR Code
            </Button>
          </div>
        )}

        {forecast && (
          <Callout tone="info" icon={Clock} title={forecast.title} role="none" className="mt-6">
            {format(new Date(forecast.time), "HH:mm", { locale: ptBR })}
          </Callout>
        )}
      </SheetBody>
    </BottomSheet>
  );
}
